import { useEffect, useRef, useState } from 'react'

import { SSE_URL } from '@/lib/config'
import { createResilientEventSource } from '@/lib/resilient-event-source'

export interface CustomerDisplayQREvent {
    type: 'customer_display_qr'
    order_id: string
    order_code: string
    customer_name: string
    total_amount: number
}

export interface CustomerDisplayPaidEvent {
    type: 'customer_display_paid'
    order_id: string
    order_code: string
    total_amount: number
    paid_amount: number
}

export interface CustomerDisplayCartItem {
    name: string
    qty: number
    price: number
    discount: number
    subtotal: number
}

export interface CustomerDisplayCartEvent {
    items: CustomerDisplayCartItem[]
    total: number
    customer_name: string
    item_count: number
}

export interface CustomerDisplayCheckoutEvent {
    order_code: string
    total: number
    paid: number
    change: number
    method: string
}

export interface CustomerDisplayHandlers {
    onShowQR?: (event: CustomerDisplayQREvent) => void
    onPaid?: (event: CustomerDisplayPaidEvent) => void
    onCart?: (event: CustomerDisplayCartEvent) => void
    onCheckout?: (event: CustomerDisplayCheckoutEvent) => void
    onClear?: () => void
}

function parseAnd<T>(handler: ((data: T) => void) | undefined) {
    return (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data as string) as T
            handler?.(data)
        } catch {
            // ignore parse errors
        }
    }
}

/**
 * SSE hook for the customer-facing display.
 * Listens for customer display events broadcast per-user.
 * Only the paired display user receives these events.
 * Connection lifecycle (reconnect, session refresh, stall watchdog) is owned
 * by the resilient EventSource wrapper — the display runs unattended.
 */
export function useCustomerDisplaySSE(handlers: CustomerDisplayHandlers = {}) {
    const [connected, setConnected] = useState(false)
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers

    useEffect(() => {
        const handle = createResilientEventSource({
            url: SSE_URL,
            onConnectedChange: setConnected,
            listeners: {
                customer_display_qr: parseAnd<CustomerDisplayQREvent>((d) =>
                    handlersRef.current.onShowQR?.(d),
                ),
                customer_display_paid: parseAnd<CustomerDisplayPaidEvent>(
                    (d) => handlersRef.current.onPaid?.(d),
                ),
                customer_display_cart: parseAnd<CustomerDisplayCartEvent>(
                    (d) => handlersRef.current.onCart?.(d),
                ),
                customer_display_checkout:
                    parseAnd<CustomerDisplayCheckoutEvent>((d) =>
                        handlersRef.current.onCheckout?.(d),
                    ),
                customer_display_clear: () => {
                    handlersRef.current.onClear?.()
                },
            },
        })

        return () => {
            handle.close()
        }
    }, [])

    return { connected }
}
